import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { X, ChevronLeft, ChevronRight, Share2, MessageCircle, MessageSquare, Link as LinkIcon, Check, Trash2 } from 'lucide-react';
import { uploadsAPI } from '../services/api';

const MAX = 4;
const dist = (t) => Math.hypot(t[0].clientX - t[1].clientX, t[0].clientY - t[1].clientY);
const clamp = (v, lo, hi) => Math.max(lo, Math.min(hi, v));
export const isTouch = () => typeof window !== 'undefined' && window.matchMedia('(hover: none), (pointer: coarse)').matches;

const ShareRow = ({ url, title, testId }) => {
  const [copied, setCopied] = useState(false);
  if (!url) return null;
  const text = `${title} — ${url}`;
  const copy = async () => { try { await navigator.clipboard.writeText(url); setCopied(true); setTimeout(() => setCopied(false), 2000); } catch { window.prompt('Copier le lien', url); } };
  const native = async () => { try { await navigator.share({ title, url }); } catch { /* cancelled */ } };
  const cls = 'inline-flex items-center gap-1 rounded-full border px-2.5 py-1 text-[11px] font-medium';
  return (
    <div className="mt-2 flex flex-wrap items-center justify-center gap-1.5" data-testid={testId ? `${testId}-zoom-share` : undefined}>
      <span className="text-[11px] text-gray-500 inline-flex items-center gap-1"><Share2 size={12} /> Partager</span>
      <a href={`https://wa.me/?text=${encodeURIComponent(text)}`} target="_blank" rel="noopener noreferrer" className={`${cls} border-green-200 text-green-700 bg-green-50`} data-testid={testId ? `${testId}-zoom-share-whatsapp` : undefined}><MessageCircle size={12} /> WhatsApp</a>
      <a href={`sms:?&body=${encodeURIComponent(text)}`} className={`${cls} border-blue-200 text-blue-700 bg-blue-50`} data-testid={testId ? `${testId}-zoom-share-sms` : undefined}><MessageSquare size={12} /> SMS</a>
      <button type="button" onClick={copy} className={`${cls} border-gray-200 text-gray-700`} data-testid={testId ? `${testId}-zoom-share-copy` : undefined}>{copied ? <Check size={12} className="text-green-600" /> : <LinkIcon size={12} />} {copied ? 'Copié' : 'Lien'}</button>
      {typeof navigator !== 'undefined' && navigator.share && <button type="button" onClick={native} className={`${cls} border-gray-200 text-gray-700`} aria-label="Plus d'options" data-testid={testId ? `${testId}-zoom-share-native` : undefined}>…</button>}
    </div>
  );
};

// Touch overlay for pictures: pinch to zoom (1x-4x), drag to pan, double-tap 2x, swipe between items, share links
const ZoomGallery = ({ items, index = 0, onClose, onIndexChange, onClearRecent, testId }) => {
  const [cur, setCur] = useState(index);
  const [tf, setTf] = useState({ s: 1, x: 0, y: 0 });
  const g = useRef({ mode: null, startDist: 0, startScale: 1, startX: 0, startY: 0, startTx: 0, startTy: 0, moved: false, lastTap: 0, tapTimer: null });
  const boxRef = useRef(null);
  const n = items.length;
  const item = items[cur] || items[0];

  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose(); if (e.key === 'ArrowRight') go(1); if (e.key === 'ArrowLeft') go(-1); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  });
  useEffect(() => () => clearTimeout(g.current.tapTimer), []);
  const skipNotify = useRef(false);
  useEffect(() => { if (skipNotify.current) { skipNotify.current = false; return; } onIndexChange && onIndexChange(cur); }, [cur]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { if (cur >= n) { skipNotify.current = true; setCur(Math.max(0, n - 1)); } }, [n]); // eslint-disable-line react-hooks/exhaustive-deps
  const hasRecent = items.some((i) => i.recent);

  const reset = () => setTf({ s: 1, x: 0, y: 0 });
  const go = (d) => { if (n < 2) return; setCur((c) => (c + d + n) % n); reset(); };
  const zoomTo = (s, cx = 0, cy = 0) => {
    const r = boxRef.current?.getBoundingClientRect();
    if (!r || s <= 1) return reset();
    const px = cx - (r.left + r.width / 2), py = cy - (r.top + r.height / 2);
    setTf({ s, x: clamp(-px * (s - 1), -r.width * (s - 1) / 2, r.width * (s - 1) / 2), y: clamp(-py * (s - 1), -r.height * (s - 1) / 2, r.height * (s - 1) / 2) });
  };

  const onStart = (e) => {
    const st = g.current;
    clearTimeout(st.tapTimer);
    st.moved = false;
    if (e.touches.length === 2) { st.mode = 'pinch'; st.startDist = dist(e.touches); st.startScale = tf.s; st.startTx = tf.x; st.startTy = tf.y; return; }
    st.mode = tf.s > 1 ? 'pan' : 'swipe';
    st.startX = e.touches[0].clientX; st.startY = e.touches[0].clientY; st.startTx = tf.x; st.startTy = tf.y;
  };
  const onMove = (e) => {
    const st = g.current;
    const r = boxRef.current?.getBoundingClientRect();
    if (!r) return;
    if (st.mode === 'pinch' && e.touches.length === 2) {
      st.moved = true;
      const s = clamp(st.startScale * (dist(e.touches) / st.startDist), 1, MAX);
      const lim = (d) => d * (s - 1) / 2;
      setTf({ s, x: clamp(st.startTx, -lim(r.width), lim(r.width)), y: clamp(st.startTy, -lim(r.height), lim(r.height)) });
      return;
    }
    if (e.touches.length !== 1) return;
    const dx = e.touches[0].clientX - st.startX, dy = e.touches[0].clientY - st.startY;
    if (Math.abs(dx) > 8 || Math.abs(dy) > 8) st.moved = true;
    if (st.mode === 'pan') {
      const lim = (d) => d * (tf.s - 1) / 2;
      setTf((t) => ({ s: t.s, x: clamp(st.startTx + dx, -lim(r.width), lim(r.width)), y: clamp(st.startTy + dy, -lim(r.height), lim(r.height)) }));
    } else if (st.mode === 'swipe' && n > 1) {
      setTf({ s: 1, x: dx, y: 0 });
    }
  };
  const onEnd = (e) => {
    const st = g.current;
    if (e.touches.length > 0) { st.mode = e.touches.length === 1 && tf.s > 1 ? 'pan' : null; st.startX = e.touches[0]?.clientX ?? 0; st.startY = e.touches[0]?.clientY ?? 0; st.startTx = tf.x; st.startTy = tf.y; return; }
    if (st.mode === 'swipe') {
      if (st.moved && Math.abs(tf.x) > 50) { go(tf.x < 0 ? 1 : -1); st.mode = null; return; }
      if (st.moved) { reset(); st.mode = null; return; }
    }
    if (st.mode === 'pinch' && tf.s < 1.05) reset();
    if (!st.moved && st.mode !== 'pinch') {
      const now = Date.now();
      const t = e.changedTouches[0];
      if (now - st.lastTap < 300) { clearTimeout(st.tapTimer); st.lastTap = 0; zoomTo(tf.s > 1 ? 1 : 2, t.clientX, t.clientY); }
      else { st.lastTap = now; if (tf.s <= 1) st.tapTimer = setTimeout(onClose, 300); }
    }
    st.mode = null;
  };

  const src = uploadsAPI.resolveUrl(item.src);
  return createPortal(
    <div onClick={onClose} className="fixed inset-0 z-[9999] bg-black/80 backdrop-blur-sm flex items-center justify-center p-3 animate-in fade-in duration-150" data-testid={testId ? `${testId}-zoom` : undefined}>
      <div onClick={(e) => e.stopPropagation()} className="relative w-full max-w-sm rounded-2xl bg-white shadow-2xl p-3 animate-in zoom-in-95 duration-150">
        <button type="button" onClick={onClose} aria-label="Fermer" className="absolute top-2 right-2 z-10 h-8 w-8 rounded-full bg-gray-100 text-gray-700 flex items-center justify-center" data-testid={testId ? `${testId}-zoom-close` : undefined}><X size={16} /></button>
        {n > 1 && <span className="absolute top-3 left-3 z-10 rounded-full bg-gray-900/80 text-white text-[11px] font-semibold px-2 py-0.5" data-testid={testId ? `${testId}-zoom-counter` : undefined}>{cur + 1} / {n}</span>}
        {item.recent && <span className="absolute top-3 right-12 z-10 rounded-full bg-sky-600 text-white text-[11px] font-semibold px-2 py-0.5" data-testid={testId ? `${testId}-zoom-recent` : undefined}>Récemment consulté</span>}
        {tf.s > 1 && <span className="absolute top-3 left-1/2 -translate-x-1/2 z-10 rounded-full bg-orange-500 text-white text-[11px] font-semibold px-2 py-0.5" data-testid={testId ? `${testId}-zoom-scale` : undefined}>×{tf.s.toFixed(1)}</span>}
        <div ref={boxRef} onTouchStart={onStart} onTouchMove={onMove} onTouchEnd={onEnd} onTouchCancel={onEnd} style={{ touchAction: 'none' }} className="w-full aspect-square overflow-hidden rounded-xl bg-white select-none" data-testid={testId ? `${testId}-zoom-stage` : undefined}>
          <img src={src} alt={item.alt || ''} draggable={false} className="no-placeholder w-full h-full object-contain will-change-transform" data-testid={testId ? `${testId}-zoom-img` : undefined}
            style={{ transform: `translate(${tf.x}px, ${tf.y}px) scale(${tf.s})`, transition: g.current.mode ? 'none' : 'transform 150ms ease-out' }} />
        </div>
        {n > 1 && (
          <>
            <button type="button" onClick={() => go(-1)} aria-label="Précédent" className="absolute left-1 top-1/2 -translate-y-1/2 h-9 w-9 rounded-full bg-white/90 shadow text-gray-800 flex items-center justify-center" data-testid={testId ? `${testId}-zoom-prev` : undefined}><ChevronLeft size={18} /></button>
            <button type="button" onClick={() => go(1)} aria-label="Suivant" className="absolute right-1 top-1/2 -translate-y-1/2 h-9 w-9 rounded-full bg-white/90 shadow text-gray-800 flex items-center justify-center" data-testid={testId ? `${testId}-zoom-next` : undefined}><ChevronRight size={18} /></button>
          </>
        )}
        {item.alt && <p className="mt-2 text-center text-sm font-semibold text-gray-900 truncate" data-testid={testId ? `${testId}-zoom-title` : undefined}>{item.alt}</p>}
        {item.recent && item.url && <a href={item.url} className="mt-1 block text-center text-xs font-medium text-sky-700 underline" data-testid={testId ? `${testId}-zoom-recent-link` : undefined}>Revenir à ce modèle</a>}
        <p className="mt-1 text-center text-[11px] text-gray-400">{tf.s > 1 ? 'Double-tap pour revenir · pincez pour zoomer' : n > 1 ? 'Glissez pour changer · pincez pour zoomer · touchez pour fermer' : 'Pincez pour zoomer · touchez pour fermer'}</p>
        <ShareRow url={item.url} title={item.shareTitle || item.alt || ''} testId={testId} />
        {hasRecent && onClearRecent && (
          <button type="button" onClick={onClearRecent} className="mt-2 mx-auto flex items-center gap-1 text-[11px] text-gray-500 hover:text-red-600" data-testid={testId ? `${testId}-zoom-clear-recent` : undefined}><Trash2 size={12} /> Vider l'historique des modèles consultés</button>
        )}
      </div>
    </div>, document.body);
};

export default ZoomGallery;
